import DarkScanGif from 'assets/images/fr-anim-dark.gif'
import LightScanGif from 'assets/images/fr-anim-light.gif'
import InfoBlock from 'components/UI/Blocks/InfoBlock'
import Button from 'components/UI/Buttons/Button'
import { Modal } from 'components/UI/Modal'
import Typography from 'components/UI/Typography'
import Heading from 'components/UI/Typography/Heading'
import { useTheme } from 'contexts/theme-context'
import { QRCodeSVG } from 'qrcode.react'
import { type ReactNode } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import tw from 'twin.macro'

interface BiometryMainBlockProps {
  isLoading: boolean
  title?: ReactNode
  subtitle?: ReactNode
  onButtonClick: () => void
}

interface BiometryModalProps {
  isOpen: boolean
  onOpenChange: (value: boolean) => void
  onContinueButtonClick: () => void
}

const BiometryMainBlock = ({ isLoading, title, subtitle, onButtonClick }: BiometryMainBlockProps) => {
  const { t } = useTranslation()

  // Context
  const { theme } = useTheme()

  return (
    <InfoBlock twStyle={tw`flex flex-col items-center relative pt-[135px] md:pt-5 dark:bg-[#202020]`}>
      <img className='bvi-hide' src={theme === 'dark' ? DarkScanGif : LightScanGif} alt='scan anim gif' />
      <Heading as='h3' variant='subtitle' twStyle={tw`text-center mt-6`}>
        {title ? title : <Trans i18nKey={'flow-video-identification'} />}
      </Heading>
      {subtitle && (
        <Typography variant='subbody' twStyle={tw`mt-3 text-grey-dark`}>
          {subtitle}
        </Typography>
      )}
      <Button variant='primary' twStyle={tw`mt-[88px] md:mt-6`} loading={isLoading} onClick={onButtonClick}>
        <Typography variant='body' weight='bold' twStyle={tw`text-white`}>
          {t('flow-continue')}
        </Typography>
        <Typography variant='caption' twStyle={tw`text-white`}>
          {t('flow-check-identification')}
        </Typography>
      </Button>
    </InfoBlock>
  )
}

const BiometryQRBlock = ({ QRValue }: { QRValue: string }) => {
  const { t } = useTranslation()

  return (
    <InfoBlock twStyle={tw`hidden sm:flex items-center sm:justify-center flex-wrap lg:flex-nowrap gap-6 mt-6`}>
      <div tw='space-y-3'>
        <Typography variant='subbody' weight='bold' twStyle={tw`font-semibold max-w-[25em]`}>
          {t('flow-no-camera')}
        </Typography>
        <ul tw='list-disc ml-5 text-[#5A5A5A] dark:text-grey-light'>
          <li>
            <Typography as='span' variant='subbody' text>
              {t('flow-open-camera')}
            </Typography>
          </li>
          <li>
            <Typography as='span' variant='subbody' text>
              {t('flow-screen-qr')}
            </Typography>
          </li>
          <li>
            <Typography as='span' variant='subbody' text>
              {t('flow-go-to-site')}
            </Typography>
          </li>
          <li>
            <Typography as='span' variant='subbody' text>
              {t('flow-identify')}
            </Typography>
          </li>
        </ul>
      </div>
      <QRCodeSVG className='qr-code bvi-no-styles' size={160} value={QRValue} />
    </InfoBlock>
  )
}

const BiometryModal = ({ isOpen, onOpenChange, onContinueButtonClick }: BiometryModalProps) => {
  const { t } = useTranslation()

  return (
    <Modal
      title={t('flow-be-sure')}
      open={isOpen}
      onClose={() => onOpenChange(false)}
      headerStyle={tw`px-5 md:px-0`}
      twStyle={tw` py-5 px-0 md:px-5`}
    >
      <ol tw='mt-6 list-decimal list-inside space-y-4 px-5 md:px-0 bg-secondary pt-5 md:pt-0 pb-4 md:pb-0'>
        <li>
          <Typography variant='body' as='span' text>
            {t('flow-be-sure-1')}
          </Typography>
        </li>
        <li>
          <Typography variant='body' as='span' text>
            {t('flow-be-sure-2')}
          </Typography>
        </li>
        <li>
          <Typography variant='body' as='span' text>
            {t('flow-be-sure-3')}
          </Typography>
        </li>
        <li>
          <Typography variant='body' as='span' text>
            {t('flow-be-sure-4')}
          </Typography>
        </li>
        <li>
          <Typography variant='body' as='span' text>
            {t('flow-be-sure-5')}
          </Typography>
        </li>
        <li>
          <Typography variant='body' as='span' text>
            {t('flow-be-sure-6')}
          </Typography>
        </li>
      </ol>
      <Button twStyle={tw`mx-auto mt-5 md:mt-8 w-full`} variant='secondary' onClick={onContinueButtonClick}>
        {t('flow-begin')}
      </Button>
    </Modal>
  )
}

export { BiometryMainBlock, BiometryModal, BiometryQRBlock }
